import React, { useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import _classNames from 'classnames';
import { useDispatch, useSelector } from 'react-redux';
import Reaptcha from 'reaptcha';
import { toast } from 'react-toastify';
import withSizes from 'react-sizes';
import { makeStyles } from '@material-ui/core/styles';
import Typography from '@material-ui/core/Typography';
import ButtonBase from '@material-ui/core/ButtonBase';
import InputBase from '@material-ui/core/InputBase';
import ArrowDropDownIcon from '@material-ui/icons/ArrowDropDown';
import ChevronRightIcon from '@material-ui/icons/ChevronRight';
import {
  makeSelectProcessing,
  makeSelectSendMailStatus,
} from 'containers/Main/selectors';
import { sendMail } from 'containers/Main/actions';

const useStyles = makeStyles({
  form: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'flex-end',
  },
  formHeader: {
    fontFamily: 'PoppinsMedium',
    textAlign: 'center',
    fontSize: 22,
    color: '#333333',
    paddingBottom: 10,
    lineHeight: '1em',
    width: '100%',
    fontWeight: '500',
    marginTop: 27,
  },
  formInput: {
    fontFamily: 'MontserratLight',
    fontWeight: '300',
    fontSize: 16,
    borderWidth: 1,
    borderStyle: 'solid',
    borderColor: 'rgba(0,0,0,0.12)',
    width: '100%',
    color: '#999999',
    padding: 16,
    marginBottom: '3%',
    marginLeft: '3%',
    backgroundColor: '#FFFFFF',
    '& > input, > textarea': {
      padding: 0,
      '&::placeholder': {
        color: '#999999',
        opacity: 1,
      },
      '&:focus': {
        color: '#3e3e3e',
      },
    },
  },
  formInputError: {
    borderColor: 'rgb(211, 84, 85)',
  },
  formSelectWrapper: {
    position: 'relative',
    width: '100%',
    marginBottom: '3%',
    marginLeft: '3%',
  },
  formSelect: {
    fontFamily: 'MontserratLight',
    fontWeight: '300',
    fontSize: 16,
    borderWidth: 1,
    borderStyle: 'solid',
    borderColor: 'rgba(0,0,0,0.12)',
    width: '100%',
    color: '#999999',
    padding: 16,
    backgroundColor: '#FFFFFF',
    '-moz-appearance': 'none',
    '-webkit-appearance': 'none',
  },
  formSelectError: {
    borderColor: 'rgb(211, 84, 85)',
  },
  formCaret: {
    position: 'absolute',
    right: 12,
    top: 'calc(50% - 12px)',
  },
  button: {
    color: '#FFFFFF',
    borderRadius: 3,
    letterSpacing: 2,
    textAlign: 'right',
    fontSize: 12,
    marginTop: '3%',
    fontFamily: 'MontserratSemiBold',
    fontWeight: '600',
    textTransform: 'uppercase',
    backgroundColor: '#E09900',
    padding: '12px 1.5em',
    transition: 'all 300ms ease 0ms',
    position: 'relative',
    '& > svg': {
      position: 'absolute',
      opacity: 0,
      marginLeft: '-1em',
      transition: 'all 300ms ease 0ms',
      height: 19,
      right: 4,
    },
    '&:hover': {
      paddingRight: '2.5em',
      paddingLeft: '1.2em',
      borderRadius: 25,
      '& > svg': {
        opacity: 1,
        marginLeft: 0,
      },
    },
  },
});

function ContactForm({ isCompact }) {
  const classes = useStyles();
  const dispatch = useDispatch();
  const processing = useSelector(makeSelectProcessing());
  const emailSent = useSelector(makeSelectSendMailStatus());
  const reaptcha = useRef();
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [subject, setSubject] = useState('');
  const [message, setMessage] = useState('');
  const [verified, setVerified] = useState(false);
  const [error, setError] = useState([]);

  useEffect(() => {
    if (emailSent) {
      toast(
        'Thank you for your message. Our team will get in contact with you.',
        { type: 'success' },
      );

      setName('');
      setEmail('');
      setSubject('');
      setMessage('');
      setVerified(false);
      reaptcha.current.reset();
    }
  }, [emailSent]);

  const onChange = e => {
    const formError = error.filter(el => el !== e.target.name);
    setError(formError);

    if (e.target.name === 'name') setName(e.target.value);
    else if (e.target.name === 'email') setEmail(e.target.value);
    else if (e.target.name === 'subject') setSubject(e.target.value);
    else if (e.target.name === 'message') setMessage(e.target.value);
  };

  const onSubmit = () => {
    const formError = [];
    if (name === '') formError.push('name');
    if (email === '') formError.push('email');
    if (subject === '') formError.push('subject');
    if (message === '') formError.push('message');
    if (!verified) formError.push('reaptcha');

    if (formError.length > 0) {
      setError(formError);
    } else {
      dispatch(
        sendMail({
          type: 'contact',
          name,
          email,
          subject,
          message,
        }),
      );
    }
  };

  return (
    <div className={classes.form}>
      <Typography className={classes.formHeader}>
        Or Send Us A Message
      </Typography>
      <InputBase
        classes={{
          root: _classNames({
            [classes.formInput]: true,
            [classes.formInputError]: error.includes('name'),
          }),
        }}
        placeholder="Name"
        name="name"
        value={name}
        onChange={onChange}
      />
      <InputBase
        classes={{
          root: _classNames({
            [classes.formInput]: true,
            [classes.formInputError]: error.includes('email'),
          }),
        }}
        placeholder="Email Address"
        name="email"
        value={email}
        onChange={onChange}
      />
      <div className={classes.formSelectWrapper}>
        <ArrowDropDownIcon className={classes.formCaret} />
        <select
          aria-label="Subject"
          name="subject"
          value={subject}
          className={_classNames({
            [classes.formSelect]: true,
            [classes.formSelectError]: error.includes('subject'),
          })}
          onChange={onChange}
        >
          <option value="">Subject</option>
          <option value="Demonstration">Demonstration</option>
          <option value="Strategic Brainstorming">
            Strategic Brainstorming
          </option>
        </select>
      </div>
      <InputBase
        placeholder="Message"
        name="message"
        multiline
        rows={6}
        classes={{
          root: _classNames({
            [classes.formInput]: true,
            [classes.formInputError]: error.includes('message'),
          }),
        }}
        value={message}
        onChange={onChange}
      />
      <Reaptcha
        ref={reaptcha}
        size={isCompact ? 'compact' : 'normal'}
        sitekey="6Lc5250UAAAAAJ91NxVgTpFG-bv1Z4Ch3x7sGzgR"
        onVerify={() => setVerified(true)}
      />
      <ButtonBase
        disableRipple
        classes={{ root: classes.button }}
        onClick={onSubmit}
      >
        {processing ? 'Sending...' : 'Submit Message'}
        <ChevronRightIcon />
      </ButtonBase>
    </div>
  );
}

const mapSizesToProps = ({ width }) => ({
  isCompact: width < 420,
});

ContactForm.propTypes = {
  isCompact: PropTypes.bool,
};

export default withSizes(mapSizesToProps)(ContactForm);
